import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || (() => {
  const host = window.location.hostname;
  const isDev = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || host.startsWith('10.') || host.startsWith('172.');
  return isDev ? `http://${host}:8000` : 'https://api.ileadx.io';
})();

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Track last activity
let lastActivity = Date.now();

// Update last activity on any user interaction
['click', 'keypress', 'scroll', 'mousemove'].forEach(event => {
  document.addEventListener(event, () => {
    lastActivity = Date.now();
  });
});

// Proactively refresh token before it expires
const scheduleTokenRefresh = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresIn = (payload.exp * 1000) - Date.now();
    const refreshTime = Math.max(expiresIn - 300000, 0);
    
    setTimeout(async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return;
      
      try {
        const res = await axios.post(`${API_BASE}/api/auth/refresh`, {
          refresh_token: refreshToken
        });
        localStorage.setItem('access_token', res.data.access_token);
        scheduleTokenRefresh();
      } catch {
        // Refresh failed,  user needs to login again
      }
    }, refreshTime);
  } catch {}
};

// Attach token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Clear all auth data
const clearAuthAndRedirect = (reason = '') => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_full_name');
  localStorage.removeItem('user_company');
  localStorage.removeItem('user_id');
  const url = reason ? `/login?reason=${reason}` : '/login';
  window.location.href = url;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error,  just reject, don't logout
    if (!error.response) {
      return Promise.reject(error);
    }

    // Skip interception for these endpoints
    if (originalRequest.url?.includes('/api/leads/export-gsheet') ||
      originalRequest.url?.includes('/api/auth/google') ||
      originalRequest.url?.includes('/api/auth/2fa') ||
      originalRequest.url?.includes('/api/auth/login') ||
      originalRequest.url?.includes('/api/auth/register') ||
      originalRequest.url?.includes('/api/billing/receipts/')) {
      return Promise.reject(error);
    }

    // 403,  Account locked
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('locked') || 
          detail.toLowerCase().includes('suspended')) {
        clearAuthAndRedirect('locked');
        return Promise.reject(error);
      }
    }

    // 401,  Token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const res = await axios.post(`${API_BASE}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          const newToken = res.data.access_token;
          localStorage.setItem('access_token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          clearAuthAndRedirect();
        }
      } else {
        clearAuthAndRedirect();
      }
    }

    return Promise.reject(error);
  }
);

// Start refresh scheduler
scheduleTokenRefresh();

export default api;